import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { leagueCustomerPushBroadcastsRoute } from "network/api-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { useGestionAccess } from "hooks/useGestionAccess";
import GestionPageHeader from "components/pages/Gestion/GestionPageHeader";
import PushBroadcastPhonePreview from "components/pages/Gestion/push/PushBroadcastPhonePreview";
import PushBroadcastHistory from "components/pages/Gestion/push/PushBroadcastHistory";
import {
  PUSH_AREA,
  emptyBroadcastForm,
  pushBlockedMessage,
  pushCharCount,
  pushErrorMessage,
  resolvePushLimits,
  validateBroadcastForm,
} from "components/pages/Gestion/push-broadcast-helpers";

const UNTOUCHED = { title: false, body: false };

/**
 * Notificaciones: compose a push for every user of the customer's app, preview it,
 * confirm and queue it, and see the history. The API enforces the area, limits and rate limit.
 */
const GestionPushBroadcasts = () => {
  const intl = useIntl();
  const { user } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const { customerId, customerName } = useGestionCustomer();
  const access = useGestionAccess();
  const allowed = access.can(PUSH_AREA);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyBroadcastForm);
  const [touched, setTouched] = useState(UNTOUCHED);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    return performRequest("GET", leagueCustomerPushBroadcastsRoute(customerId), {}, true, user)
      .then((response) => {
        if (mounted.current) setStatus(response.data);
      })
      .catch(() => {
        if (mounted.current) enqueueSnackbar(intl.formatMessage({ id: "gestion.error.generic" }), { variant: "error" });
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
  }, [customerId, user, enqueueSnackbar, intl]);

  useEffect(() => {
    if (!allowed || !customerId) return;
    load();
  }, [allowed, customerId, load]);

  if (access.loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box sx={{ py: 4 }}>
        <RevaText variant="h5" sx={{ mb: 1 }}>
          {intl.formatMessage({ id: "gestion.access.forbidden.title" })}
        </RevaText>
        <RevaText variant="body1" color="text.secondary">
          {intl.formatMessage({ id: "gestion.access.forbidden.body" })}
        </RevaText>
      </Box>
    );
  }

  const limits = resolvePushLimits(status?.limits);
  const errors = validateBroadcastForm(form, status?.limits);
  const blocked = status ? pushBlockedMessage(status.reason, status.limits) : null;
  const canSend = Boolean(status?.can_send) && Object.keys(errors).length === 0 && !sending;
  const appName = status?.app?.name || customerName;
  const audience = intl.formatMessage({ id: "gestion.push.audience.all" }, { customer: appName });
  const fieldsDisabled = !status?.can_send || sending;

  const setField = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const markTouched = (field) => () => setTouched((prev) => ({ ...prev, [field]: true }));

  const submit = () => {
    setTouched({ title: true, body: true });
    if (canSend) setConfirmOpen(true);
  };

  const send = async () => {
    setSending(true);
    try {
      const response = await performRequest(
        "POST",
        leagueCustomerPushBroadcastsRoute(customerId),
        { title: form.title.trim(), body: form.body.trim() },
        true,
        user,
      );
      enqueueSnackbar(intl.formatMessage({ id: "gestion.push.queued" }), { variant: "success" });
      setStatus((prev) => ({
        ...(prev || {}),
        limits: response.data?.limits ?? prev?.limits,
        broadcasts: [response.data?.broadcast, ...(prev?.broadcasts ?? [])].filter(Boolean),
      }));
      setForm(emptyBroadcastForm());
      setTouched(UNTOUCHED);
      setConfirmOpen(false);
      load(); // refresh can_send/reason and statuses (the job may already have finished)
    } catch (error) {
      const message = pushErrorMessage(error, status?.limits);
      enqueueSnackbar(intl.formatMessage({ id: message?.id ?? "gestion.error.generic" }, message?.values), {
        variant: "error",
      });
      setConfirmOpen(false);
      if (message) load(); // the server state changed (limit reached, credentials removed)
    } finally {
      setSending(false);
    }
  };

  const renderField = (field, max, props = {}) => {
    const count = pushCharCount(form[field]);
    const error = touched[field] ? errors[field] : null;
    return (
      <Box>
        <TextField
          label={intl.formatMessage({ id: `gestion.push.form.${field}` })}
          fullWidth
          value={form[field]}
          onChange={setField(field)}
          onBlur={markTouched(field)}
          disabled={fieldsDisabled}
          error={Boolean(error)}
          helperText={error ? intl.formatMessage({ id: error.id }, error.values) : undefined}
          inputProps={{ "data-testid": `push-${field}` }}
          {...props}
        />
        <RevaText
          variant="caption"
          data-testid={`push-${field}-counter`}
          sx={{ display: "block", textAlign: "right", mt: 0.5, color: count > max ? "error.main" : "text.secondary" }}
        >
          {intl.formatMessage({ id: "gestion.push.form.counter" }, { count, max })}
        </RevaText>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto", p: { xs: 2, md: 4 } }}>
      <GestionPageHeader
        title={intl.formatMessage({ id: "gestion.push.title" })}
        subtitle={intl.formatMessage({ id: "gestion.push.subtitle" })}
      />

      {blocked ? (
        <Alert
          severity={status.reason === "rate_limited" ? "warning" : "info"}
          data-testid="push-blocked"
          sx={{ mt: 3 }}
        >
          {intl.formatMessage({ id: blocked.id }, blocked.values)}
        </Alert>
      ) : null}

      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box>
                <RevaText variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {intl.formatMessage({ id: "gestion.push.audience.label" })}
                </RevaText>
                <RevaText variant="body1" data-testid="push-audience" sx={{ fontWeight: 700 }}>
                  {audience}
                </RevaText>
              </Box>
              {renderField("title", limits.titleMax)}
              {/* No maxLength on the inputs: it counts UTF-16 units and would cut emoji early. */}
              {renderField("body", limits.bodyMax, { multiline: true, minRows: 3 })}
              {limits.remaining !== null && limits.perHour !== null && status?.can_send ? (
                <RevaText variant="caption" color="text.secondary">
                  {intl.formatMessage(
                    { id: "gestion.push.form.remaining" },
                    { remaining: limits.remaining, perHour: limits.perHour },
                  )}
                </RevaText>
              ) : null}
              <Box>
                <Button variant="contained" data-testid="push-submit" onClick={submit} disabled={!canSend}>
                  {intl.formatMessage({ id: "gestion.push.form.submit" })}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <PushBroadcastPhonePreview
            appName={appName}
            appImage={status?.app?.image ?? null}
            title={form.title}
            body={form.body}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <PushBroadcastHistory rows={status?.broadcasts ?? []} loading={loading} onRefresh={load} />
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => !sending && setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ "data-testid": "push-confirm" }}
      >
        <DialogTitle>{intl.formatMessage({ id: "gestion.push.confirm.title" })}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <RevaText variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {intl.formatMessage({ id: "gestion.push.audience.label" })}
              </RevaText>
              <RevaText variant="body2" data-testid="push-confirm-audience" sx={{ fontWeight: 700 }}>
                {audience}
              </RevaText>
            </Box>
            <Box>
              <RevaText variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                {form.title.trim()}
              </RevaText>
              <RevaText variant="body2" sx={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>
                {form.body.trim()}
              </RevaText>
            </Box>
            <RevaText variant="caption" color="text.secondary">
              {intl.formatMessage({ id: "gestion.push.confirm.body" })}
            </RevaText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>
            {intl.formatMessage({ id: "gestion.push.confirm.cancel" })}
          </Button>
          <Button variant="contained" data-testid="push-confirm-send" onClick={send} disabled={sending}>
            {sending ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            {intl.formatMessage({ id: "gestion.push.confirm.send" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionPushBroadcasts;
